import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { History, Maximize2, Plus, X } from 'lucide-react';
import { ICON_STROKE } from '../components/Layout';
import { STEWARD } from '../components/WithSteward';
import { useAssistant } from './useAssistant';
import { AiConsentCopy, AiConsentActions, AiKindNotice } from './AiConsentModal';
import { Composer } from './Composer';
import { MessageList, ThinkingIndicator } from './MessageList';
import { UsageMeter, UsageNotices } from './UsageMeter';
import { WARN_AT } from './constants';
import { useAssistantStarters } from './useAssistantStarters';
import { usagePct } from './format';

/**
 * The assistant, docked to the right of the page instead of replacing it (docs/24).
 *
 * "What is this account?" and "why did this move?" are questions you ask *while looking at*
 * a page. Asking one used to cost you the page. This keeps the record and the answer on
 * screen together; the full `/ask` page still owns long sessions and thread history.
 *
 * It owns no conversation state — it is the second view onto `AssistantProvider` (P1), so it
 * and the page can never disagree about consent, the active thread, or remaining quota.
 * What lives here is this surface's own behaviour: its scroll container, its focus rules,
 * and a consent presentation that does not throw a modal over the record you are reading.
 *
 * Non-modal on purpose (P8): no focus trap, Tab moves freely between the panel and the page.
 * Working with both at once is the entire point; a trap would make this a modal dialog
 * wearing a sidebar costume.
 */

const cls = {
  // `top-16` / `100vh-4rem` are the top bar's 64px, and that coupling is deliberate — see the
  // block comment on `TopBar` in `components/Layout.tsx`, which names this line back
  // (steward-app#585). The panel sits UNDER the header now rather than beside it.
  //
  // Sticky still works without any change to how the app scrolls: this element's natural
  // position is the header's bottom edge, so it only pins once the header has scrolled past.
  //
  // `100vh-4rem` fits the viewport exactly ONLY while that natural top really is 64px, which
  // is why `OfflineBanner` was moved inside the page column rather than left in flow above
  // this row (see Layout.tsx). Anything in flow above the row pushes this element down by its
  // height and hangs the same amount below the fold — starting with the composer, which is
  // the panel's only input.
  panel:    'hidden xl:flex w-[380px] shrink-0 flex-col sticky top-16 h-[calc(100vh-4rem)] bg-surface border-l border-line shadow-rail-l',
  // 40px, not the 64px it used to be. That height existed to line this row up with the top
  // bar, back when the two sat side by side; underneath it, a matching row would just be a
  // second bar of chrome. Slimming it does not win the space back — the old header was
  // *parallel* to the top bar and so cost nothing — it only limits what this change spends.
  header:   'flex shrink-0 items-center gap-1 border-b border-line px-3 h-10',
  // Sized for the h-10 row, not a restoration of the 15px title the taller header carried.
  // `truncate` because the brand short name is configurable and four icon buttons plus the
  // padding already claim ~150px of the 380px.
  title:    'font-serif text-[14px] font-normal m-0 text-navy truncate',
  iconBtn:  'flex h-8 w-8 shrink-0 items-center justify-center rounded-token border-none bg-transparent cursor-pointer text-slate transition-colors duration-150 hover:bg-cream hover:text-navy',
  body:     'flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin',
  intro:    'text-[13px] text-slate leading-relaxed m-0',
  chip:     'rounded-full border border-line-strong bg-transparent px-3 py-1.5 text-left text-[12px] text-navy cursor-pointer transition-colors duration-150 hover:border-bronze/50 hover:bg-cream',
  footer:   'shrink-0 border-t border-line bg-cream px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
  consent:  'rounded-token border border-line bg-cream p-4',
  consentH: 'm-0 mb-3 font-serif text-[15px] font-normal text-navy outline-none',
};

/** The id the top-bar trigger points `aria-controls` at, and the element focus returns to
 *  when the panel closes. One constant so the two can't drift apart. */
export const ASSISTANT_PANEL_ID = 'assistant-panel';
export const ASSISTANT_TRIGGER_ID = 'assistant-panel-trigger';


export function AssistantPanel() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const starters = useAssistantStarters();
  const {
    messages, sending, sendStartedAt, loadingThread,
    input, setInput, budget, limitReached,
    consentAcknowledged, profileLoaded, kindLoadFailed, retryKind, acceptConsent,
    send, stop, canStop, startNewChat, closePanel, savesChats, firmConversation,
  } = useAssistant();

  const panelRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const consentRef = useRef<HTMLHeadingElement>(null);
  const kindNoticeRef = useRef<HTMLParagraphElement>(null);

  const showConsent = profileLoaded && !consentAcknowledged;

  // Focus on open goes to the one thing the user can act on: the composer, or the consent
  // card's heading for someone who has not consented yet. A panel that appears beside the
  // page without moving focus is invisible to a screen reader until they hunt for it.
  //
  // Keyed on `profileLoaded` too, not just `showConsent`. On a restored-open panel this
  // effect runs before `GET /me` settles, when the composer is still disabled and `.focus()`
  // on a disabled element does nothing at all — without the second key it would never try
  // again, and the panel would come back focusless.
  useEffect(() => {
    if (kindLoadFailed) kindNoticeRef.current?.focus();
    else if (showConsent) consentRef.current?.focus();
    else textareaRef.current?.focus();
  }, [showConsent, profileLoaded, kindLoadFailed]);

  // Escape closes and hands focus back to the control that opened us — but only when the
  // user is actually *in* the panel.
  //
  // A non-modal surface listening on `document` otherwise fights everything else that binds
  // the same key: AccountMenu, WorkspaceSwitcher, MultiSelect, AttachPicker and the folder
  // context menu are all dismissible-on-Escape and none of them are `aria-modal`, so one
  // press would close the popup AND the panel. Worse, with nothing open at all it would rip
  // focus out of whatever page field the user was typing in and drop it on the top bar.
  // Checking focus containment is the whole rule: Escape belongs to whatever has focus.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const active = document.activeElement;
      if (!active || !panelRef.current?.contains(active)) return;
      closePanel();
      document.getElementById(ASSISTANT_TRIGGER_ID)?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePanel]);

  // Follow the conversation, but only for a reader who is already at the bottom of it.
  //
  // Two departures from the page, both deliberate: this scrolls its own container rather
  // than calling `scrollIntoView` on a sentinel (that walks ancestors, and an arriving
  // answer must never nudge the document being read beside it), and it yields to a user who
  // has scrolled up rather than yanking them back down mid-read.
  const atBottomRef = useRef(true);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !atBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, sending, loadingThread]);

  function onScroll() {
    const el = bodyRef.current;
    if (!el) return;
    // 24px of slack: "at the bottom" should survive sub-pixel heights and a stray wheel tick.
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }

  function goToPage() {
    // The panel doesn't render on /ask (P3); navigating carries the conversation.
    //
    // `from` is the return ticket: the page's "Dock to the side panel" button sends the user
    // back to the record they were reading with the panel re-docked, rather than to a guess.
    // Browser Back does the same thing without it — this is for the user who reaches for a
    // control instead of the chrome.
    navigate('/ask', { state: { from: pathname } });
  }

  async function onAcceptConsent() {
    await acceptConsent();
    textareaRef.current?.focus();
  }

  // The meter is transparency; the notices are consequences. In a 380px column the full
  // footer stack is ~150px of permanent chrome before a single message, so the meter earns
  // its place only once usage is actually worth knowing (P11). The page keeps it always-on.
  const showMeter = budget?.quota != null && usagePct(budget.used, budget.quota) >= WARN_AT;

  return (
    <aside
      id={ASSISTANT_PANEL_ID}
      ref={panelRef}
      aria-label={`Ask ${STEWARD}`}
      className={cls.panel}
    >
      <div className={cls.header}>
        {/* Visible again (#585 dropped it, #596 puts it back). The case for dropping it was
            that the top-bar Ask control sits above the panel in its active state and already
            reads "Ask Steward" — true, but the two never share a line of the viewport, and a
            strip of four unlabelled icon buttons reads as a floating toolbar rather than the
            top of a panel. It is sized to the slimmed h-10 row rather than restoring the old
            min-h-16 header, so it spends no message space over the header that replaced it.
            The accessible name is unaffected either way: it comes from the `aria-label` on
            the `aside`, not from this heading. */}
        <h2 className={cls.title}>Ask {STEWARD}</h2>
        <div className="ml-auto flex shrink-0 items-center">
          <button type="button" className={cls.iconBtn} onClick={startNewChat} aria-label="New chat" title="New chat">
            <Plus size={16} strokeWidth={ICON_STROKE} aria-hidden />
          </button>
          {/* History is page-only in v1 (P6). The affordance still lives here, because once
              the top-bar control toggles a panel, yesterday's conversation is three clicks
              deep with nothing on screen suggesting saved chats exist — and a New chat
              button with no counterpart implies chats are disposable. */}
          {/* Not where chats are not saved (none since 2026-10-26; a firm's own workspace was
              one, steward-app#1815 until #1817). */}
          {savesChats && (
            <button type="button" className={cls.iconBtn} onClick={goToPage} aria-label="Chat history" title="Chat history">
              <History size={16} strokeWidth={ICON_STROKE} aria-hidden />
            </button>
          )}
          <button type="button" className={cls.iconBtn} onClick={goToPage} aria-label="Open the full page" title="Open the full page">
            <Maximize2 size={15} strokeWidth={ICON_STROKE} aria-hidden />
          </button>
          <button type="button" className={cls.iconBtn} onClick={closePanel} aria-label="Close the assistant" title="Close">
            <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
          </button>
        </div>
      </div>

      <div className={cls.body} ref={bodyRef} onScroll={onScroll}>
        {/* First-use consent, inline (P7, amending docs/15 C1). Same words as the page's
            modal, different presentation: a page-blocking overlay thrown over the record you
            are reading, because you clicked a side panel, serves the consent purpose no
            better and reads as hostile. "Maybe later" closes the panel and leaves the page
            exactly as it was. */}
        {kindLoadFailed ? (
          <div className={cls.consent}><AiKindNotice onRetry={retryKind} focusRef={kindNoticeRef} /></div>
        ) : showConsent ? (
          <div className={cls.consent}>
            <h3 className={cls.consentH} tabIndex={-1} ref={consentRef}>Before you use the assistant</h3>
            <AiConsentCopy firm={firmConversation} />
            <AiConsentActions onAccept={onAcceptConsent} onDecline={closePanel} />
          </div>
        ) : (
          <>
            {messages.length === 0 && !loadingThread && (
              <div>
                <p className={cls.intro}>
                  {starters.kind === 'firm' ? (
                    <>
                      Ask about the page you&apos;re on, your clients and prospects, or how
                      something in {STEWARD} works. Your records stay on screen beside the answer.
                    </>
                  ) : (
                    <>
                      Ask about the page you&apos;re on, your accounts and documents, or how
                      something in {STEWARD} works. Your data stays on screen beside the answer.
                    </>
                  )}
                </p>
                <div className="mt-4 flex flex-col items-start gap-2">
                  {starters.suggestions.map((q) => (
                    <button key={q} type="button" className={cls.chip} onClick={() => void send(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {loadingThread && <ThinkingIndicator />}
            <MessageList messages={messages} />
            {sending && <ThinkingIndicator startedAt={sendStartedAt ?? undefined} />}
          </>
        )}
      </div>

      <div className={cls.footer}>
        {showMeter && <UsageMeter budget={budget} />}
        <UsageNotices budget={budget} />
        <Composer
          ref={textareaRef}
          value={input}
          onChange={setInput}
          onSend={() => void send()}
          onStop={canStop ? stop : undefined}
          sending={sending}
          disabled={sending || limitReached || !consentAcknowledged}
          limitReached={limitReached}
          rows={2}
        />
      </div>
    </aside>
  );
}
